import { useMemo } from "react";
import he from "he";
import { Blog } from "../../redux/types/blogTypes";
import { formatDate } from "../../utils";
import "./BlogArticle.css";

/** Reorganize existing Firestore HTML for presentation without replacing its copy. */
function arrangeArticle(html: string) {
  const doc = new DOMParser().parseFromString(he.decode(html), "text/html");
  const guide = doc.querySelector(".seller-guide");
  const intro = guide?.querySelector(".seller-guide__intro");
  const video = guide?.querySelector(".seller-guide__video");
  let introduction = "";
  let media = "";
  if (intro && video) {
    const secondParagraph = intro.nextElementSibling;
    introduction = intro.outerHTML;
    if (secondParagraph?.tagName === "P") {
      introduction += secondParagraph.outerHTML;
      secondParagraph.remove();
    }
    intro.remove();
    media = video.outerHTML;
    video.remove();
  }
  const why = guide?.querySelector('[aria-labelledby="why-fiddlepiddle"]');
  const image = guide?.querySelector(".seller-guide__image");
  const benefits = why?.querySelector(".seller-guide__benefits");
  if (why && image) {
    const story = doc.createElement("div");
    story.className = "seller-guide__story";
    why.before(story);
    story.append(why, image);
    if (benefits) story.after(benefits);
  }
  const commissionHeading = guide?.querySelector("#seller-commission");
  const heading = commissionHeading?.textContent || "";
  const rate = heading.match(/\d+(?:\.\d+)?%/);
  if (commissionHeading && rate && rate.index !== undefined) {
    const number = doc.createElement("span");
    number.className = "seller-guide__rate";
    number.textContent = rate[0];
    commissionHeading.replaceChildren(
      doc.createTextNode(heading.slice(0, rate.index)), number,
      doc.createTextNode(heading.slice(rate.index + rate[0].length)),
    );
  }
  const questions = guide?.querySelector('[aria-labelledby="seller-questions"]');
  questions?.querySelectorAll("h3").forEach((heading) => {
    const answer = heading.nextElementSibling;
    if (answer?.tagName !== "P") return;
    const card = doc.createElement("div");
    card.className = "seller-guide__question";
    heading.before(card);
    card.append(heading, answer);
  });
  return { introduction, media, body: doc.body.innerHTML };
}

/** Used only for the Start Selling article by the existing blog renderer. */
export function BlogArticle({ blog }: { blog: Blog }) {
  const content = useMemo(() => arrangeArticle(blog.blogContent), [blog.blogContent]);
  const milliseconds = blog.createdTime.toString().length > 10
    ? blog.createdTime : blog.createdTime * 1000;
  return (
    <article className="seller-editorial" aria-labelledby="blog-article-title">
      <header className="seller-editorial__hero">
        <div className="seller-editorial__welcome">
          <span className="seller-editorial__eyebrow">The seller’s guide</span>
          <h1 id="blog-article-title">{blog.blogTitle}</h1>
          <p className="seller-editorial__date">
            Published <time dateTime={new Date(milliseconds).toISOString()}>
              {formatDate(blog.createdTime)}
            </time>
          </p>
          <div className="seller-editorial__introduction"
            dangerouslySetInnerHTML={{ __html: content.introduction }} />
        </div>
        {content.media && (
          <div className="seller-editorial__film"
            dangerouslySetInnerHTML={{ __html: content.media }} />
        )}
      </header>
      <div className="seller-editorial__body"
        dangerouslySetInnerHTML={{ __html: content.body }} />
    </article>
  );
}
